/**
 * "What to pull out of long answers" — the extraction field editor, Story 20.2.
 *
 * Visual Reference: `design/screens/editor-qualify.html` — the card between
 * Criteria and Disqualifiers, in the default, empty, error, rules and 375px
 * captures.
 *
 * RULE-021 is the reason this card exists: "The Qualify UI asks what to pull out
 * of an answer, never whether an answer is good." A field is a NAME, a TYPE with
 * its range or values, the QUESTION it reads, and — in the operator's own words
 * — what to pull out. Nothing here rates an answer, and
 * `tests/extraction-copy.test.ts` holds every word of it to that.
 *
 * DRAWN, CLOSED AND OPEN. The mockup draws the card closed — the rows, the
 * count, `Add a field`, the empty state and the rules-only line — and, since the
 * 2026-09-17 round-trip, the editor `Add a field` opens (`field-editor`). Story
 * 20.2 built that editor before it was drawn, from the controls the tab already
 * uses — `Field`, `Select`, `Button` — and the drawing kept every label it
 * composed. Story 20.10 added the one line it lacked and moved its words to
 * DRAWN in `extraction-copy.ts`.
 *
 * EVERY EDIT GOES THROUGH `edit()`, and every rule comes from `extraction.ts`,
 * which takes them from the contract.
 */
import { useId, useState } from 'react'
import { EXTRACTION_FIELD_LIMIT, type ExtractionField, type Funnel } from '@leadgible/schema'
import { Button } from '../ui/Button'
import { Field } from '../ui/Field'
import { Select } from '../ui/Select'
import { COMPOSED, DRAWN, TYPE_LABEL, TYPE_ORDER, fieldsCount } from './extraction-copy'
import {
  DESCRIPTION_MAX,
  NAME_MAX,
  addExtractionField,
  buildField,
  emptyDraft,
  extractionSources,
  joinNames,
  questionOf,
  rangeText,
  readersOf,
  removalRefusal,
  removeExtractionField,
  type FieldDraft,
  type FieldIssues,
} from './extraction'
import { edit } from './store'

export function ExtractionCard({ funnel, rulesOnly }: { funnel: Funnel; rulesOnly: boolean }) {
  const fields = funnel.rubric.extraction
  const [adding, setAdding] = useState(false)
  // The field whose removal was just refused, so its row can say why.
  const [refusedKey, setRefusedKey] = useState<string | null>(null)

  const remove = (fieldKey: string) => {
    if (removalRefusal(funnel, fieldKey) !== null) {
      setRefusedKey(fieldKey)
      return
    }
    setRefusedKey(null)
    edit('Remove an extraction field', (draft) => removeExtractionField(draft, fieldKey))
  }

  return (
    <section className="flex flex-col gap-mdx-20 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card">
      <div className="flex flex-col gap-mdx-6">
        <div className="flex flex-wrap items-baseline justify-between gap-x-mdx-16 gap-y-mdx-8">
          <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">{DRAWN.title}</span>
          <span className="text-mdx-13 text-mdx-slate tabular-nums">
            {fieldsCount(fields.length, EXTRACTION_FIELD_LIMIT)}
          </span>
        </div>
        <span className="max-w-mdx-measure-56 text-mdx-15 text-mdx-slate">{DRAWN.lead}</span>
        {/*
          The rules-only capture's own line. The rows stay, because a field that
          is not being read right now is still defined, and Story 18.8's claim is
          that nothing here stops working.
        */}
        {rulesOnly ? <span className="text-mdx-15 text-mdx-slate">{DRAWN.notRead}</span> : null}
      </div>

      {fields.length === 0 && !adding ? (
        <Empty onAdd={() => setAdding(true)} />
      ) : (
        <>
          {fields.length > 0 ? (
            <div className="flex flex-col gap-mdx-8">
              {fields.map((field) => (
                <FieldRow
                  key={field.field_key}
                  funnel={funnel}
                  field={field}
                  refused={refusedKey === field.field_key}
                  onRemove={() => remove(field.field_key)}
                />
              ))}
            </div>
          ) : null}

          {adding ? (
            <FieldEditor funnel={funnel} onClose={() => setAdding(false)} />
          ) : fields.length >= EXTRACTION_FIELD_LIMIT ? (
            <span className="text-mdx-13 text-mdx-slate">{COMPOSED.atLimit(EXTRACTION_FIELD_LIMIT)}</span>
          ) : (
            <Button className="self-start" onClick={() => setAdding(true)}>
              {DRAWN.add}
            </Button>
          )}
        </>
      )}
    </section>
  )
}

/** The empty capture: a quiet mark, what is missing, what to do about it. */
function Empty({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center gap-mdx-14 px-mdx-8 py-mdx-48 text-center">
      <span aria-hidden="true" className="size-mdx-40 flex-none rounded-mdx-control bg-mdx-line" />
      <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">{DRAWN.emptyTitle}</span>
      <span className="max-w-mdx-measure-52 text-mdx-15 text-mdx-slate">{DRAWN.emptyBody}</span>
      <Button onClick={onAdd}>{DRAWN.add}</Button>
    </div>
  )
}

/**
 * One field, as drawn: the key, its type, its range or its values, and Remove on
 * the first line; the question it reads and what scores it on the second.
 */
function FieldRow({
  funnel,
  field,
  refused,
  onRemove,
}: {
  funnel: Funnel
  field: ExtractionField
  refused: boolean
  onRemove: () => void
}) {
  const refusalId = useId()
  const question = questionOf(funnel.blocks.find((block) => block.id === field.block_id))
  const { criteria } = readersOf(funnel, field.field_key)
  // Recomputed rather than remembered: once the criterion is gone, so is the
  // refusal, without anything having to clear it.
  const refusal = refused ? removalRefusal(funnel, field.field_key) : null
  const range = rangeText(field)

  return (
    <div className="flex flex-col gap-mdx-8 rounded-mdx-control bg-mdx-ground px-mdx-16 py-mdx-12">
      <div className="flex flex-wrap items-center gap-x-mdx-12 gap-y-mdx-10">
        <span className="min-w-0 text-mdx-15 font-mdx-semibold">{field.field_key}</span>
        <span className="inline-flex flex-none items-center rounded-mdx-pill bg-mdx-nurture-tint px-mdx-12 py-mdx-5 text-mdx-13 font-mdx-semibold text-mdx-slate">
          {TYPE_LABEL[field.type]}
        </span>
        {field.type === 'enum' && field.enum_values ? (
          <span className="flex flex-wrap gap-mdx-6">
            {field.enum_values.map((value) => (
              <span
                key={value}
                className="rounded-mdx-control-sm border border-mdx-line bg-mdx-white px-mdx-8 py-mdx-3 text-mdx-13 text-mdx-ink"
              >
                {value}
              </span>
            ))}
          </span>
        ) : range !== null ? (
          <span className="text-mdx-15 text-mdx-slate tabular-nums">{range}</span>
        ) : null}
        {/*
          The same Remove the Disqualifiers card draws — a destructive action, and
          so the one place `--mdx-error` belongs (PRD §1.9). Its name carries the
          key because there is one per row.
        */}
        <button
          type="button"
          aria-label={`${DRAWN.remove} ${field.field_key}`}
          aria-describedby={refusal !== null ? refusalId : undefined}
          onClick={onRemove}
          className="ml-auto flex-none cursor-pointer rounded-mdx-control-sm border border-mdx-line bg-mdx-white px-mdx-16 py-mdx-8 text-mdx-13 font-mdx-semibold text-mdx-error"
        >
          {DRAWN.remove}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-x-mdx-14 gap-y-mdx-6 text-mdx-13 text-mdx-slate">
        {question !== null ? (
          <>
            <span>
              {DRAWN.from} <span className="font-mdx-semibold text-mdx-ink">{`"${question}"`}</span>
            </span>
            <span aria-hidden="true">·</span>
          </>
        ) : null}
        {criteria.length > 0 ? (
          <span>
            {DRAWN.scoredBy} <span className="font-mdx-semibold text-mdx-ink">{joinNames(criteria)}</span>
          </span>
        ) : (
          <span>{DRAWN.notScored}</span>
        )}
      </div>

      {refusal !== null ? (
        <span id={refusalId} role="alert" className="text-mdx-13 text-mdx-error">
          {refusal}
        </span>
      ) : null}
    </div>
  )
}

/**
 * The editor `Add a field` opens — the `field-editor` capture (Story 20.10).
 *
 * It asks for what the contract needs: a name, what to pull out, a type with
 * its range or values, and the question to read. The source select offers only
 * questions `crossReference` will accept, so AC 2's refusal is a choice the
 * operator is never given rather than an error they meet after saving.
 */
function FieldEditor({ funnel, onClose }: { funnel: Funnel; onClose: () => void }) {
  const sources = extractionSources(funnel)
  const [draft, setDraft] = useState<FieldDraft>(() => emptyDraft(funnel))
  const [issues, setIssues] = useState<FieldIssues>({})
  const descriptionId = useId()
  const headingId = `${descriptionId}-heading`
  const descriptionHelperId = `${descriptionId}-helper`
  const descriptionErrorId = `${descriptionId}-error`
  const set = (patch: Partial<FieldDraft>) => setDraft((current) => ({ ...current, ...patch }))

  const submit = () => {
    const result = buildField(draft, funnel)
    if (!result.ok) {
      setIssues(result.issues)
      return
    }
    edit('Add an extraction field', (next) => addExtractionField(next, result.field))
    onClose()
  }

  return (
    // As drawn: a heading, 20px in, and Cancel before the primary action. The
    // group is named by the heading, so a screen reader enters "New field".
    <div
      role="group"
      aria-labelledby={headingId}
      className="flex flex-col gap-mdx-16 rounded-mdx-control border border-mdx-line p-mdx-20"
    >
      <span id={headingId} className="text-mdx-15 font-mdx-semibold text-mdx-ink">
        {DRAWN.newField}
      </span>
      <Field
        label={DRAWN.name}
        value={draft.name}
        maxLength={NAME_MAX}
        error={issues.name}
        onChange={(event) => set({ name: event.target.value })}
      />

      <div className="flex flex-col gap-mdx-6">
        <label htmlFor={descriptionId} className="text-mdx-14 font-mdx-semibold text-mdx-ink">
          {DRAWN.description}
        </label>
        <textarea
          id={descriptionId}
          value={draft.description}
          maxLength={DESCRIPTION_MAX}
          rows={3}
          aria-invalid={issues.description !== undefined ? true : undefined}
          // The helper, then the error, as `Field` binds them.
          aria-describedby={[descriptionHelperId, issues.description !== undefined ? descriptionErrorId : null]
            .filter(Boolean)
            .join(' ')}
          onChange={(event) => set({ description: event.target.value })}
          className={[
            'min-h-mdx-textarea-min rounded-mdx-control border bg-mdx-white px-mdx-14 py-mdx-10 text-mdx-15 text-mdx-ink outline-none',
            issues.description !== undefined ? 'border-mdx-error' : 'border-mdx-line focus:border-mdx-brand',
          ].join(' ')}
        />
        <span id={descriptionHelperId} className="text-mdx-13 text-mdx-slate">
          {DRAWN.descriptionHelper}
        </span>
        {issues.description !== undefined ? (
          <span id={descriptionErrorId} className="text-mdx-13 text-mdx-error">
            {issues.description}
          </span>
        ) : null}
      </div>

      <Select
        label={DRAWN.type}
        showLabel
        value={draft.type}
        onChange={(event) => set({ type: event.target.value as FieldDraft['type'] })}
      >
        {TYPE_ORDER.map((type) => (
          <option key={type} value={type}>
            {TYPE_LABEL[type]}
          </option>
        ))}
      </Select>

      {draft.type === 'number' ? (
        <div className="flex flex-col gap-mdx-6">
          <div className="flex flex-wrap gap-mdx-12">
            <Field
              label={DRAWN.lowest}
              type="number"
              value={draft.lowest}
              onChange={(event) => set({ lowest: event.target.value })}
            />
            <Field
              label={DRAWN.highest}
              type="number"
              value={draft.highest}
              onChange={(event) => set({ highest: event.target.value })}
            />
          </div>
          {issues.range !== undefined ? (
            <span className="text-mdx-13 text-mdx-error">{issues.range}</span>
          ) : null}
        </div>
      ) : null}

      {draft.type === 'enum' ? (
        <Field
          label={DRAWN.values}
          helper={DRAWN.valuesHelper}
          value={draft.values}
          error={issues.values}
          onChange={(event) => set({ values: event.target.value })}
        />
      ) : null}

      <div className="flex flex-col gap-mdx-6">
        {sources.length > 0 ? (
          <Select
            label={DRAWN.source}
            showLabel
            value={draft.blockId}
            onChange={(event) => set({ blockId: event.target.value })}
          >
            <option value="">{DRAWN.chooseSource}</option>
            {sources.map((block) => (
              <option key={block.id} value={block.id}>
                {questionOf(block)}
              </option>
            ))}
          </Select>
        ) : null}
        <span className="text-mdx-13 text-mdx-slate">{DRAWN.sourceHelper}</span>
        {issues.source !== undefined ? (
          <span className="text-mdx-13 text-mdx-error">{issues.source}</span>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-mdx-12 pt-mdx-4">
        <Button variant="secondary" onClick={onClose}>
          {DRAWN.cancel}
        </Button>
        <Button onClick={submit}>{DRAWN.submit}</Button>
      </div>
    </div>
  )
}
